import { type FC, useState, useReducer, createContext, lazy, Suspense, useEffect } from "react";
import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import type { PrefectureModalProps } from "./src/prefecture_modal";
import { initialState, formStateReducer, type formStateProps, type ActionProps } from "./modules";
import { PrefectureOuter } from "./src/prefecture_outer";
import * as styles from "./src/styles.css";

const PrefectureModal = lazy(() =>
  retryLazyLoad<PrefectureModalProps>({
    fn: () =>
      import("./src/prefecture_modal").then((module) => ({ default: module.PrefectureModal })),
  }),
);

type areaDataProps = {
  prefecture?: number;
  cities?: number[];
  line?: string | number;
  stations?: number[];
};

type initialAreaDataProps = {
  prefecture: {
    id: number;
    name: string;
  };
  cities: {
    id: number;
    name: string;
  }[];
  line: {
    id: number;
    name: string;
  };
  stationsArray: {
    id: number;
    name: string;
  }[];
  showStations: boolean;
  stations: {
    id: number;
    name: string;
  }[];
};

type Props = {
  prefectureArray: {
    areaName: string;
    prefectureChildArray: {
      prefName: string;
      prefId: number;
    }[];
  }[];
  cityFetchUrl: string;
  lineFetchUrl: string;
  stationFetchUrl: string;
  initialFormState: initialAreaDataProps[];
  formName: string;
};

type formStateContextProps = {
  formState: formStateProps;
  dispatchFormState: React.Dispatch<ActionProps>;
};

export const formStateContext = createContext({} as formStateContextProps);

export const ScoutsSelectLocations: FC<Props> = ({
  prefectureArray,
  cityFetchUrl,
  lineFetchUrl,
  stationFetchUrl,
  initialFormState,
  formName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 選択した条件を管理するstate
  const [formState, dispatchFormState] = useReducer(formStateReducer, initialState);
  // 送信用のデータを管理するstate
  const [areaData, setAreaData] = useState<areaDataProps[]>([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState<Record<number, boolean>>({});

  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!initialFormState) {
      return;
    }

    for (let i = 0; i < Object.keys(initialFormState).length; i += 1) {
      const prefectureId = initialFormState[i].prefecture.id;
      dispatchFormState({
        type: "selectPref",
        value: Number(prefectureId),
        name: String(initialFormState[i].prefecture.name),
      });

      initialFormState[i].cities.forEach((city) => {
        dispatchFormState({
          type: "selectCities",
          value: city.id,
          name: city.name,
          prefectureId,
        });
      });

      initialFormState[i].stations.forEach((station) => {
        dispatchFormState({
          type: "selectStations",
          value: station.id,
          name: station.name,
          prefectureId,
        });
      });

      if (Object.keys(initialFormState[i].line).length > 0) {
        dispatchFormState({
          type: "selectLine",
          value: initialFormState[i].line.id,
          name: initialFormState[i].line.name,
          prefectureId,
        });
      }

      const stationValues: [string, number][] = Object.keys(initialFormState[i].stationsArray)
        .map(Number)
        .map((key) => [
          initialFormState[i].stationsArray[key].name,
          initialFormState[i].stationsArray[key].id,
        ]);

      if (initialFormState[i].stationsArray.length > 0) {
        dispatchFormState({
          type: "saveStations",
          value: stationValues,
          prefectureId,
        });
      }

      dispatchFormState({
        type: "toggleShowStations",
        value: initialFormState[i].showStations,
        prefectureId,
      });
    }
  }, [initialFormState]);

  useEffect(() => {
    const areaInfo = [];
    for (let i = 0; i < Object.keys(formState).length; i += 1) {
      const prefectureId = Object.keys(formState).map(Number)[i];
      const thisAreaInfo: areaDataProps = {
        prefecture: prefectureId,
      };

      if (Object.keys(formState[prefectureId].cities).length > 0) {
        thisAreaInfo.cities = Object.keys(formState[prefectureId].cities).map(Number);
      }

      // stationが設定されている場合には、lineは設定しない
      if (Object.keys(formState[prefectureId].stations).length > 0) {
        thisAreaInfo.stations = Object.keys(formState[prefectureId].stations).map(Number);
      } else if (
        Object.keys(formState[prefectureId].line).length > 0 &&
        !formState[prefectureId].showStations
      ) {
        thisAreaInfo.line = Number(Object.keys(formState[prefectureId].line)[0]);
      }
      areaInfo.push(thisAreaInfo);
    }
    setAreaData(areaInfo);
  }, [formState, initialFormState]);

  return (
    <>
      <formStateContext.Provider value={{ formState, dispatchFormState }}>
        {Object.keys(formState)
          .map(Number)
          .map((prefectureId) => (
            <PrefectureOuter
              key={prefectureId}
              prefectureId={prefectureId}
              cityFetchUrl={cityFetchUrl}
              lineFetchUrl={lineFetchUrl}
              stationFetchUrl={stationFetchUrl}
            />
          ))}
        <input
          type="hidden"
          name={`form_client_frontend_scout_user_search[${formName}]`}
          id={`form_client_frontend_scout_user_search_${formName}`}
          value={areaData.length > 0 ? JSON.stringify(areaData) : ""}
          className="js-search-scoutable-field-disabled-if-value-empty"
        />
        <button className={styles.openPrefectureModalButton} type="button" onClick={openModal}>
          <i className="icon-plus-1" />
          都道府県を追加する
        </button>
        <Suspense fallback={null}>
          <PrefectureModal
            prefectureArray={prefectureArray}
            closeModal={closeModal}
            setIsAccordionOpen={setIsAccordionOpen}
            isAccordionOpen={isAccordionOpen}
            isModalOpen={isModalOpen}
          />
        </Suspense>
      </formStateContext.Provider>
    </>
  );
};
